import { ListModel } from "shared/interfaces/core-interfaces";
import yup from 'shared/validation/yupExtensions';

export interface HolidayListResponse {
    holidays: HolidayResponse[];
}

export interface Holiday {
    name: string;
    from: Date | null;
    to: Date | null;
    isPublic: boolean;
    isReligious: boolean;
    typeId: number;
    durationTypeId: number;
    description?: string | null;
    tenantId: string;
}

export interface HolidayResponse extends Holiday {
    id: string;
    type: string;
    durationType: number;
    tenant: number;
    year: number;
    creatorId: string;
    creator: string;
    editorId: string;
    editor: string;
    createdAt: Date;
    editedAt: Date;
    isActive: boolean;
}

export interface HolidayRequest extends Holiday {
    id?: string;
}

export interface HolidaysBatchRequest {
    holidays: HolidayRequest[];
}

export interface HolidayCreateDataResponse {
    types: ListModel<number>[];
    durationTypes: ListModel<number>[];
    tenants: ListModel<string>[];
}

export interface HolidayState {
    holiday: HolidayResponse | null;
    activeHolidays: HolidayResponse[];
    inactiveHolidays: HolidayResponse[];
    createData: HolidayCreateDataResponse | null;
}

export const initialHolidayState: HolidayState = {
    holiday: null,
    activeHolidays: [],
    inactiveHolidays: [],
    createData: null,
};

export const initHolidayRequest: HolidayRequest = {
    name: '',
    from: null,
    to: null,
    isPublic: false,
    isReligious: false,
    typeId: 0,
    durationTypeId: 0,
    tenantId: ""
};

export const initHolidaysBatchRequest: HolidaysBatchRequest = {
    holidays: []
};

export const HolidayModelScheme = yup.object().shape({
    id: yup.string(),
    name: yup.string().noLeadingSpace().required("Name is required.").max(150, "Name is too long."),
    from: yup.date().typeError("Please type valid date.").required("From date is required.").nullable()
        .test(
            'not-null',
            'From date is required.',
            value => value !== null
        )
        .test(
            "from-less-than-or-equal-to-to",
            "From date cannot be later than to date.",
            function (value) {
                const { to } = this.parent;
                if (to && value) {
                    return value <= to;
                }
                return true;
            }
        ),
    to: yup.date().typeError("Please type valid date.").required("To date is required.").nullable()
        .test(
            'not-null',
            'To date is required.',
            value => value !== null
        )
        .test(
            "to-greater-than-from",
            'To date cannot be earlier than from date.',
            function (value) {
                const { from } = this.parent;
                if (from && value) {
                    return value >= from;
                }
                return true;
            }
        ),
    isPublic: yup.boolean().defined(),
    isReligious: yup.boolean().defined(),
    typeId: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(1, "Type is required.").integer("Type is required.").required("Type is required."),
    durationTypeId: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Duration is required.").required("Duration is required."),
    description: yup.string().noLeadingSpace().max(2000, "Description is too long.").nullable(),
    tenantId: yup.string().required("Tenant is required."),
})

export const HolidaysBatchModelScheme = yup.object().shape({
    holidays: yup.array().of(HolidayModelScheme).defined().min(1, "At least one holiday is required.")
});